import React, { useState, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { Spin, Empty, Typography, Card } from "antd";
import { getMailingResponseData } from "../../../core/api/mailingResponse";

const { Title } = Typography;

/**
 * Component to display mailing response data in a table
 * @param {Object} props - Component props
 * @param {number} props.mediaPlanId - ID of the media plan
 */
const MailingResponseTable = ({ mediaPlanId }) => {
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
console.log(mediaPlanId, " mediaPlanId MailingResponseTable");
  useEffect(() => {
    const fetchMailingResponseData = async () => {
      if (!mediaPlanId) {
        console.warn("MailingResponseTable: mediaPlanId is undefined");
        return;
      }

      setLoading(true);
      try {
        const response = await getMailingResponseData({mediaPlanId});
        if (response.data && response.data.results) {
          setRowData(response.data.results);
          
          // Dynamically generate columns from the first result
          if (response.data.results.length > 0) {
            const columns = generateColumns();
            setColumnDefs(columns);
          }
        }
      } catch (error) {
        console.error("Error fetching mailing response data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMailingResponseData();
  }, [mediaPlanId]);

  // Generate column definitions based on data
  const generateColumns = () => {
    const baseColumns = [
      {
        headerName: "List",
        field: "list_details.name",
        sortable: true,
        filter: true,
        width: 150,
      },
      {
        headerName: "Segment",
        field: "segment_details.name",
        sortable: true,
        filter: true,
        width: 150,
        valueGetter: (params) => {
          return params.data.segment_details ? params.data.segment_details.name : "N/A";
        },
      },
      {
        headerName: "Source Code",
        field: "source_code",
        sortable: true,
        filter: true,
        width: 150,
      },
    ];

    // Mailing metrics
    const mailingMetricsColumns = [
      {
        headerName: "Mail Qty",
        field: "mail_qty",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
      },
      {
        headerName: "Shipped Qty",
        field: "ship_qty",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
      },
      {
        headerName: "Net %",
        field: "net_percentage",
        sortable: true,
        filter: true,
        width: 100,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value ? `${params.value.toFixed(2)}%` : "";
        },
      },
    ];

    // Order metrics
    const orderMetricsColumns = [
      // {
      //   headerName: "Buyers",
      //   field: "buyers",
      //   sortable: true,
      //   filter: true,
      //   width: 120,
      //   type: "numericColumn",
      // },
      {
        headerName: "Gross Orders",
        field: "gross_orders",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
      },
      // {
      //   headerName: "Net Orders",
      //   field: "net_orders",
      //   sortable: true,
      //   filter: true,
      //   width: 120,
      //   type: "numericColumn",
      // },
      {
        headerName: "Refunds",
        field: "refunds",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
      },
    ];

    // Sales metrics
    const salesMetricsColumns = [
      {
        headerName: "Gross Sales",
        field: "gross_sales",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value ? `$${params.value.toFixed(2)}` : "";
        },
      },
      {
        headerName: "Net Sales",
        field: "net_sales",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value ? `$${params.value.toFixed(2)}` : "";
        },
      },
      {
        headerName: "Avg Sale Value",
        field: "average_sale_value",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value ? `$${params.value.toFixed(2)}` : "";
        },
      },
    ];

    // Cost metrics
    const costMetricsColumns = [
      {
        headerName: "Cost/Mail",
        field: "cost_per_mail",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value ? `$${params.value.toFixed(2)}` : "";
        },
      },
      {
        headerName: "Printer Cost",
        field: "printer_cost",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value ? `$${params.value.toFixed(2)}` : "";
        },
      },
      {
        headerName: "Postage Cost",
        field: "postage_cost",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value ? `$${params.value.toFixed(2)}` : "";
        },
      },
      {
        headerName: "Letter Shop Cost",
        field: "letter_shop_cost",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value ? `$${params.value.toFixed(2)}` : "";
        },
      },
      {
        headerName: "Data Processing Cost",
        field: "data_processing_cost",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value ? `$${params.value.toFixed(2)}` : "";
        },
      },
      {
        headerName: "Misc Fee",
        field: "misc_fee",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value ? `$${params.value.toFixed(2)}` : "";
        },
      },

      {
        headerName: "Product Cost",
        field: "product_cost",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value ? `$${parseFloat(params.value).toFixed(2)}` : "";
        },
      },
      {
        headerName: "Shipping  Cost",
        field: "shipping_cost",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value ? `$${parseFloat(params.value).toFixed(2)}` : "";
        },
      },
      {
        headerName: "Fulfillment Cost",
        field: "fulfillment_cost",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value ? `$${parseFloat(params.value).toFixed(2)}` : "";
        },
      },

      {
        headerName: "Refund Cost",
        field: "refund_cost",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value ? `$${parseFloat(params.value).toFixed(2)}` : "";
        },
      },
      {
        headerName: "Call Center Cost",
        field: "call_center_cost",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value ? `$${parseFloat(params.value).toFixed(2)}` : "";
        },
      },
      {
        headerName: "Merchant Fee",
        field: "merchant_fee",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value ? `$${parseFloat(params.value).toFixed(2)}` : "";
        },
      },
      {
        headerName: "Royalty Cost",
        field: "royalty_cost",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value ? `$${parseFloat(params.value).toFixed(2)}` : "";
        },
      },
      {
        headerName: "Total Cost",
        field: "total_cost",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value ? `$${params.value.toFixed(2)}` : "";
        },
      },
      
    ];

    // Performance metrics
    const performanceMetricsColumns = [
      {
        headerName: "Net P/L",
        field: "net_profit_loss",
        sortable: true,
        filter: true,
        width: 120,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value ? `$${params.value.toFixed(2)}` : "";
        },
      },
      {
        headerName: "ROI",
        field: "net_roi",
        sortable: true,
        filter: true,
        width: 100,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value ? `${params.value.toFixed(2)}%` : "";
        },
      },
      {
        headerName: "Profit %",
        field: "profit_percentage",
        sortable: true,
        filter: true,
        width: 100,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value ? `${params.value.toFixed(2)}%` : "";
        },
      },
    ];

    // Combine all column groups
    return [
      ...baseColumns,
      ...mailingMetricsColumns,
      ...orderMetricsColumns,
      ...salesMetricsColumns,
      ...costMetricsColumns,
      ...performanceMetricsColumns,
    ];
  };

  const defaultColDef = {
    resizable: true,
    sortable: true,
    filter: true,
  };

  return (
    <Card title={<Title level={4}>Mailing Response Data</Title>} style={{ marginTop: 20 }}>
      {loading ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spin size="large" />
        </div>
      ) : rowData.length > 0 ? (
        <div className="ag-theme-quartz" style={{ height: 500, width: "100%" }}>
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={10}
          />
        </div>
      ) : (
        <Empty description="No mailing response data available" />
      )}
    </Card>
  );
};

export default MailingResponseTable;
